import { useHasPermission, useIsFeatureWhitelisted } from 'mf_core/auth';
import { useFeatureFlagBase } from './use-feature-flag-base';
import { useMemo } from 'react';
import { useCMPPaymentTerm } from './useCMPPaymentTerm';

const boKeys = {
  CMP_CC_Enabled: 'CMP_CreditCardPopup_Enable',
  CMP_CC_TermCode: 'CMP_CreditCardTermCode',
  CMP_DocData_ForcePopup_Enabled: 'Is_Docdata_MP_PopUp_Enabled',
  CMP_WorldPay_ForcePopup_Enabled: 'Is_Worldpay_MP_PopUp_Enabled',
  Docdata_CMP_TermCode: 'Docdata_DD_CMP_TermCode',
  Is_Cybersource_Force_Popup_Enabled: 'Is_Cybersource_Force_Popup_Enabled',
  Is_DocData_enabled: 'Is_DocData_DD_enabled',
  Is_Worldpay_enabled: 'Is_Worldpay_enabled',
  Worldpay_CMP_TermCode: 'Worldpay_CMP_TermCode',
};

export type Gateway = 'CYBERSOURCE' | 'DOCDATA' | 'WORLDPAY';

export const useCMPCardCheck = () => {
  const { boValues, isReady: isFlagLoaded } = useFeatureFlagBase();
  const isFeatureWhitelisted = useIsFeatureWhitelisted();
  const isCmpWhitelisted = isFeatureWhitelisted('CMP');
  const hasPermission = useHasPermission();
  const canPlaceOrder = hasPermission(['PlaceOrders']);

  const cmpEnabledInBO = useMemo(
    () => isFlagLoaded && isCmpWhitelisted,
    [isCmpWhitelisted, isFlagLoaded]
  );
  const cmpTermCodes = useMemo(
    () =>
      boValues[boKeys.CMP_CC_TermCode]
        ?.split(';')
        .map((code: any) => code.trim()?.toLowerCase())
        .filter(Boolean),
    [boValues]
  );
  const allCmpTermCodes = cmpTermCodes?.includes('*');

  const worldPayTermCodes = useMemo(
    () =>
      boValues[boKeys.Worldpay_CMP_TermCode]
        ?.split(';')
        .map((code: any) => code.trim()?.toLowerCase())
        .filter(Boolean),
    [boValues]
  );
  const allWorldPayTermCodes = worldPayTermCodes?.includes('*');

  const docDataTermCodes = useMemo(
    () =>
      boValues[boKeys.Docdata_CMP_TermCode]
        ?.split(';')
        .map((code: any) => code.trim()?.toLowerCase())
        .filter(Boolean),
    [boValues]
  );
  const allDocDataTermCodes = docDataTermCodes?.includes('*');

  const { data: cmpPaymentTerm } = useCMPPaymentTerm(
    cmpEnabledInBO &&
      canPlaceOrder &&
      (boValues[boKeys.CMP_CC_Enabled]?.toLowerCase() === 'true' ||
        boValues[boKeys.Is_Worldpay_enabled]?.toLowerCase() === 'true' ||
        boValues[boKeys.Is_DocData_enabled]?.toLowerCase() === 'true')
  );

  const enableCybersource = useMemo(
    () =>
      cmpEnabledInBO &&
      boValues[boKeys.CMP_CC_Enabled]?.toLowerCase() === 'true' &&
      (allCmpTermCodes ||
        cmpTermCodes?.includes(cmpPaymentTerm?.name?.toLowerCase())),
    [
      allCmpTermCodes,
      boValues,
      cmpEnabledInBO,
      cmpTermCodes,
      cmpPaymentTerm?.name,
    ]
  );

  const enableWorldPay = useMemo(
    () =>
      cmpEnabledInBO &&
      boValues[boKeys.Is_Worldpay_enabled]?.toLowerCase() === 'true' &&
      (allWorldPayTermCodes ||
        worldPayTermCodes?.includes(cmpPaymentTerm?.name?.toLowerCase())),
    [
      allWorldPayTermCodes,
      boValues,
      cmpEnabledInBO,
      cmpPaymentTerm?.name,
      worldPayTermCodes,
    ]
  );

  const enableDocData = useMemo(
    () =>
      cmpEnabledInBO &&
      boValues[boKeys.Is_DocData_enabled]?.toLowerCase() === 'true' &&
      (allDocDataTermCodes ||
        docDataTermCodes?.includes(cmpPaymentTerm?.name?.toLowerCase())),
    [
      allDocDataTermCodes,
      boValues,
      cmpEnabledInBO,
      cmpPaymentTerm?.name,
      docDataTermCodes,
    ]
  );
  const enableForcePopupDocdata =
    boValues[boKeys.CMP_DocData_ForcePopup_Enabled]?.toLowerCase() === 'true';

  const enableForcePopupWorldPay =
    boValues[boKeys.CMP_WorldPay_ForcePopup_Enabled]?.toLowerCase() === 'true';
  const enableForcePopupCybersource =
    boValues[boKeys.Is_Cybersource_Force_Popup_Enabled]?.toLowerCase() ===
    'true';
  const gateway = useMemo((): Gateway | undefined => {
    if (enableWorldPay) return 'WORLDPAY';
    else if (enableDocData) return 'DOCDATA';
    else if (enableCybersource) return 'CYBERSOURCE';
  }, [enableCybersource, enableDocData, enableWorldPay]);

  return {
    enableCybersource,
    enableDocData,
    enableForcePopupCybersource,
    enableForcePopupDocdata,
    enableForcePopupWorldPay,
    enableWorldPay,
    gateway,
    isFlagLoaded,
  };
};
